import { useState } from "react";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";
import { submitContactForm } from "../../data/mock";
import { toast } from "sonner";
import { Send } from "lucide-react";

export const CallToAction = () => {
  const [ctaRef, ctaVisible] = useScrollAnimation();
  const [formRef, formVisible] = useScrollAnimation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.message) {
      toast.error("Please fill in email and message.");
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await submitContactForm(formData);
      if (result.success) {
        toast.success("Message sent! We'll be in touch soon.");
        setFormData({ name: "", email: "", message: "" });
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* CTA Section */}
      <section
        ref={ctaRef}
        className={`bg-[#4A7CC9] transition-all duration-700 ${
          ctaVisible ? "opacity-100" : "opacity-0"
        }`}
      >
        <div className="max-w-[920px] mx-auto px-5 md:px-10 py-[80px] md:py-[100px] lg:py-[120px] text-center">
          <h2 className="text-[clamp(26px,4vw,40px)] font-semibold leading-[1.2] tracking-tight text-white mb-6">
            Ready to build with clarity?
          </h2>
          <p className="text-[clamp(16px,2vw,18px)] leading-[1.65] text-white/75 mb-10 max-w-[440px] mx-auto">
            Book a 30-minute strategy conversation.
            <br />
            No pitch. Just alignment.
          </p>
          <a
            href="https://calendly.com/thefussedbulb/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center bg-white hover:bg-[#F7F7F5] text-[#0A0A0A] font-semibold text-[15px] px-8 py-[14px] rounded-full transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] no-underline tracking-wide"
          >
            Book a Strategy Call
          </a>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="bg-[#F7F7F5]">
        <div className="max-w-[1180px] mx-auto px-5 md:px-10">
          <div className="h-px bg-[rgba(10,10,10,0.08)]" />
        </div>

        <div
          ref={formRef}
          className={`max-w-[600px] mx-auto px-5 md:px-10 py-[60px] md:py-[80px] lg:py-[100px] transition-all duration-700 ${
            formVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-6"
          }`}
        >
          <h3 className="text-[24px] font-semibold text-[#0A0A0A] mb-2">
            Or, drop us a line.
          </h3>
          <p className="text-[15px] text-[rgba(10,10,10,0.4)] mb-8">
            We'll get back within 24 hours.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Your name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full px-4 py-3.5 bg-white border border-[rgba(10,10,10,0.1)] rounded-lg text-[15px] text-[#0A0A0A] placeholder:text-[rgba(10,10,10,0.28)] outline-none focus:border-[#4A7CC9] transition-colors duration-200 font-[inherit]"
              />
              <input
                type="email"
                placeholder="Email address"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                required
                className="w-full px-4 py-3.5 bg-white border border-[rgba(10,10,10,0.1)] rounded-lg text-[15px] text-[#0A0A0A] placeholder:text-[rgba(10,10,10,0.28)] outline-none focus:border-[#4A7CC9] transition-colors duration-200 font-[inherit]"
              />
            </div>
            <textarea
              placeholder="Tell us about your project"
              value={formData.message}
              onChange={(e) =>
                setFormData({ ...formData, message: e.target.value })
              }
              required
              rows={4}
              className="w-full px-4 py-3.5 bg-white border border-[rgba(10,10,10,0.1)] rounded-lg text-[15px] text-[#0A0A0A] placeholder:text-[rgba(10,10,10,0.28)] outline-none focus:border-[#4A7CC9] transition-colors duration-200 resize-none font-[inherit]"
            />
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <a
                href="mailto:letsrock@thefussedbulb.com"
                className="text-[14px] text-[rgba(10,10,10,0.4)] hover:text-[#4A7CC9] transition-colors duration-200 no-underline"
              >
                letsrock@thefussedbulb.com
              </a>
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white font-semibold text-[14px] px-6 py-3 rounded-full transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed border-none cursor-pointer font-[inherit]"
              >
                {isSubmitting ? "Sending..." : "Send"}
                <Send className="h-3.5 w-3.5" />
              </button>
            </div>
          </form>
        </div>
      </section>
    </>
  );
};
